import React, { useEffect, useState } from "react";
import {
  ActionIcon,
  Box,
  Group,
  Text,
  Title,
  UnstyledButton,
} from "@mantine/core";
import {
  IconAdjustmentsHorizontal,
  IconChevronLeft,
  IconCopy,
  IconDots,
  IconShare,
  IconPencil,
  IconTrash,
} from "@tabler/icons-react";
import { useGuardedNavigate } from "../../../context/NavigationGuardContext";
import { REPORTS_BASE_PATH } from "../shell/reportsShellContext";
import type { CanvasHeaderProps } from "../v2/canvasTypes";
import MobileActionsSheet, { SheetAction } from "./sheets/MobileActionsSheet";
import MobileMetaSheet from "./sheets/MobileMetaSheet";
import styles from "./MobileReportHeader.module.css";

/** "‹ Reports": back to the phone report list, through the unsaved-changes
 *  guard. */
export function MobileBackLink() {
  const navigate = useGuardedNavigate();
  return (
    <UnstyledButton
      type="button"
      className={styles.back}
      aria-label="Back to reports"
      onClick={() => navigate(REPORTS_BASE_PATH)}
    >
      <IconChevronLeft size={18} aria-hidden />
      Reports
    </UnstyledButton>
  );
}

/** The phone's back link on its own (a report that no longer exists). */
export function MobileBackLinkBar() {
  return (
    <Box className={styles.backBar}>
      <MobileBackLink />
    </Box>
  );
}

export interface MobileReportHeaderProps {
  /** Name / description / Remove wiring (shared with the desktop header). */
  header: CanvasHeaderProps;
  onDuplicate: () => void;
  duplicateLabel?: string;
  /** More "…" actions, listed after Duplicate (Customize, Export, Print). */
  extraActions?: SheetAction[];
  /** Shows the Customize icon button beside "…". */
  onCustomize?: () => void;
  /** Muted line under the description (e.g. "Updated …"). */
  meta?: React.ReactNode;
  /** Under the title (e.g. the chip row). */
  toolbar?: React.ReactNode;
}

/**
 * A report's header on a phone (ReportHeader on desktop): back link,
 * Customize / "…" buttons, name, description and meta. The "…" sheet offers
 * Edit name & description, Duplicate, Share (when the caller may share),
 * any extra actions and Remove from
 * list. A just-added copy opens the name sheet straight away (autoEdit).
 * The report body is its sibling, so swapping headers at the breakpoint
 * leaves the body mounted.
 */
export default function MobileReportHeader({
  header,
  onDuplicate,
  duplicateLabel = "Duplicate",
  extraActions = [],
  onCustomize,
  meta,
  toolbar,
}: MobileReportHeaderProps) {
  const [sheet, setSheet] = useState<"actions" | "meta" | null>(null);
  const { autoEdit, onAutoEditHandled } = header;

  useEffect(() => {
    if (!autoEdit) return;
    setSheet("meta");
    onAutoEditHandled?.();
  }, [autoEdit, onAutoEditHandled]);

  const actions: SheetAction[] = [
    {
      key: "edit",
      label: "Edit name & description",
      icon: <IconPencil size={17} aria-hidden />,
      onClick: () => setSheet("meta"),
    },
    {
      key: "duplicate",
      label: duplicateLabel,
      icon: <IconCopy size={17} aria-hidden />,
      onClick: onDuplicate,
    },
    ...(header.onShare
      ? [
          {
            key: "share",
            label: "Share",
            icon: <IconShare size={17} aria-hidden />,
            onClick: header.onShare,
          },
        ]
      : []),
    ...extraActions,
    {
      key: "remove",
      label: "Remove from list",
      icon: <IconTrash size={17} aria-hidden />,
      danger: true,
      onClick: header.onRemove,
    },
  ];

  return (
    <>
      <Box className={styles.header}>
        <Group gap="xs" wrap="nowrap">
          <MobileBackLink />
          <Box className={styles.spacer} />
          {onCustomize && (
            <ActionIcon
              variant="default"
              size="lg"
              className={styles.iconButton}
              aria-label="Customize view"
              onClick={onCustomize}
            >
              <IconAdjustmentsHorizontal size={17} aria-hidden />
            </ActionIcon>
          )}
          <ActionIcon
            variant="default"
            size="lg"
            className={styles.iconButton}
            aria-label="More options"
            onClick={() => setSheet("actions")}
          >
            <IconDots size={17} aria-hidden />
          </ActionIcon>
        </Group>
        <Title order={2} className={styles.title}>
          {header.name}
        </Title>
        {header.description && (
          <Text className={styles.description}>{header.description}</Text>
        )}
        {header.sharedNote && (
          <Text size="xs" c="dimmed">
            {header.sharedNote}
          </Text>
        )}
        {meta && <Text className={styles.meta}>{meta}</Text>}
        {toolbar}
      </Box>
      <MobileActionsSheet
        opened={sheet === "actions"}
        onClose={() => setSheet(null)}
        title={header.name}
        actions={actions}
      />
      <MobileMetaSheet
        opened={sheet === "meta"}
        onClose={() => setSheet(null)}
        name={header.name}
        description={header.description}
        defaultName={header.defaultName}
        onSave={header.onSaveMeta}
      />
    </>
  );
}
